import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IMergedHeadersReturn } from './index.headers';
import { IAdminxStore } from '../../stores';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import { useEffect } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { IConflictData } from '../../models/bidder-domains.model';
import { CollapsibleText } from '../../components/collapsible-text/collapsible-text';
import { DispatchLogsTypes } from '@monorepo/controlled/src/hooks/use-logs';
import { useDownload } from '@monorepo/tools/src/lib/hooks/tools/use-download';
import { HttpStore } from '@monorepo/controlled/src/stores/http.store';
import { BidderDomainsApi } from '../../apis/bidder-domains.api';

const columnHelper = createColumnHelper<IConflictData>();

export const useDomainOverlapHeaders = (selectedCampaignId?: number): IMergedHeadersReturn<IConflictData> => {
	const { settingsStore, toastsStore } = useStores<IAdminxStore>();
	const { currentRouteWithoutSpecialChars } = useRoute();
	const tableStore = settingsStore.pages.get(currentRouteWithoutSpecialChars)?.tableStore;

	useEffect(() => {
		if (tableStore && Object.keys(tableStore.getColumnVisibility()).length === 0) {
			tableStore?.setDefaultColumnVisibility({
				campaign_id: true,
				shared_values: true,
				conflict_type: true,
			});
			tableStore?.setColumnVisibility(tableStore.getDefaultColumnVisibility());
		}
	}, [tableStore]);

	const downloadConflictData = (selectedId: number, conflictId: number) => {
		toastsStore.addToast({ msg: 'Starting your download...', type: DispatchLogsTypes.Info });

		useDownload({
			httpStore: new HttpStore<undefined, File | null>({
				httpFunc: () => BidderDomainsApi.downloadCampaignConflictData(selectedId, conflictId) as unknown as Promise<File | null>,
			}),
			fileName: `${selectedId}_vs_${conflictId}_conflict_data_${new Date()
				.toISOString()
				.substring(0, 19)
				.replace('T', ' ')
				.replace(/:/g, '-')}`,
			extension: 'csv',
		}).catch(err => {
			toastsStore.addToast({ msg: 'Download Failed, try again later.', type: DispatchLogsTypes.Error });
			console.error('Download error:', err);
		});
	};

	return {
		columns: [
			columnHelper.accessor('campaign_id', {
				header: 'Conflicting Campaign ID',
				cell: ({ row }) => <span>{row.getValue('campaign_id')}</span>,
			}),
			columnHelper.accessor('shared_values', {
				header: 'Shared Values',
				cell: info => {
					const { row } = info;
					const sharedValues = row.getValue('shared_values') as string[] | string;
					return <CollapsibleText text={sharedValues} />;
				},
			}),
			columnHelper.accessor('conflict_type', {
				header: 'Type Of Conflict',
				cell: ({ row }) => {
					const conflictType = row.getValue('conflict_type') as string;
					return <span>{conflictType}</span>;
				},
			}),
			columnHelper.accessor('domains_counter', {
				header: 'Domains',
				cell: ({ row }) => {
					const domainsCounter = row.getValue('domains_counter') as number;
					const conflictCampaignId = row.getValue('campaign_id') as number;
					const selectedCampaignIdLocal = selectedCampaignId || 0;
					if (domainsCounter < 1) {
						return <span>No Domains To Download</span>;
					} else {
						return (
							<div className="links">
								<a
									onClick={() =>
										downloadConflictData(selectedCampaignIdLocal, conflictCampaignId)
									}>{`[${domainsCounter}]`}</a>
							</div>
						);
					}
				},
			}),
		],
	};
};
